import { History } from "lucide-react";
import { useEffect, useState } from "react";

import { timeAgo } from "@/lib/format";
import type { FormDraftApi } from "@/lib/form-drafts";

import { Button } from "./Button";

/** "Continue where you left off": shown above a form when the person has a saved draft of it. */
export function DraftOffer<T>({
  draft,
  onContinue,
}: {
  draft: FormDraftApi<T>;
  onContinue: (values: T) => void;
}) {
  if (!draft.offer) return null;
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-3 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2.5 text-sm text-sky-900 dark:text-sky-200"
    >
      <History className="size-4 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1">
        <span className="font-medium">Continue where you left off?</span> You have a draft from{" "}
        {timeAgo(draft.offer.saved_at)}.
      </p>
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          onClick={() => {
            const values = draft.restore();
            if (values) onContinue(values);
          }}
        >
          Continue
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={draft.startFresh}>
          Start fresh
        </Button>
      </div>
    </div>
  );
}

/** "Draft saved 1 min ago", kept current while the form stays open. */
export function DraftSavedNote({ savedAt }: { savedAt: string | null }) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!savedAt) return;
    const t = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, [savedAt]);
  if (!savedAt) return null;
  return <p className="text-xs text-fg-muted">Draft saved {timeAgo(savedAt)}</p>;
}
